import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Star,
  MapPin,
  Award,
  Users,
  CheckCircle,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import api from '../../services/authService';
import MarketingReveal from './MarketingReveal';

/**
 * Vendor spotlight — same data + carousel behavior, marketing B&W texture.
 * Keeps API spotlights, features, stats, and profile CTAs.
 */
const VendorSpotlight = ({
  vendors = null,
  title = 'Vendor Spotlight',
  subtitle = 'Featured professional of the month',
  autoRotate = true,
  rotateInterval = 8000,
}) => {
  const { data: spotlightsResponse, isLoading } = useQuery({
    queryKey: ['vendorSpotlights'],
    queryFn: async () => {
      const response = await api.get('/vendor-spotlights', {
        params: { limit: 10 },
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const transformSpotlightData = (spotlight) => ({
    id: spotlight.id,
    name: spotlight.name,
    category: spotlight.category,
    description: spotlight.description,
    rating: spotlight.rating || 0,
    reviewCount: spotlight.review_count || 0,
    completedEvents: spotlight.completed_events || 0,
    location: spotlight.location,
    image:
      spotlight.image ||
      'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=800&h=600&fit=crop',
    profileImage:
      spotlight.profile_image ||
      'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop',
    features: spotlight.features || [],
    badge: spotlight.badge || '',
    yearsInBusiness: spotlight.years_in_business || 0,
    startingPrice: spotlight.starting_price || 0,
    vendorId: spotlight.vendor_id,
  });

  const apiSpotlights = spotlightsResponse?.data
    ? spotlightsResponse.data.map(transformSpotlightData)
    : [];

  const spotlightVendors = vendors || apiSpotlights;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (spotlightVendors.length > 0 && currentIndex >= spotlightVendors.length) {
      setCurrentIndex(0);
    }
  }, [spotlightVendors.length, currentIndex]);

  const handleNext = () => {
    if (isAnimating || spotlightVendors.length < 2) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % spotlightVendors.length);
    setTimeout(() => setIsAnimating(false), 450);
  };

  const handlePrev = () => {
    if (isAnimating || spotlightVendors.length < 2) return;
    setIsAnimating(true);
    setCurrentIndex(
      (prev) => (prev - 1 + spotlightVendors.length) % spotlightVendors.length
    );
    setTimeout(() => setIsAnimating(false), 450);
  };

  useEffect(() => {
    if (!autoRotate || spotlightVendors.length <= 1) return undefined;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % spotlightVendors.length);
    }, rotateInterval);
    return () => clearInterval(interval);
  }, [autoRotate, rotateInterval, spotlightVendors.length]);

  const spotlightVendor = spotlightVendors[currentIndex];

  if (isLoading && !vendors) {
    return (
      <section className="border-b border-m-border-light bg-m-canvas-light py-20 sm:py-24">
        <div className="page-container animate-pulse px-4 sm:px-6">
          <div className="mb-10 h-10 w-56 rounded bg-m-border-light" />
          <div className="grid overflow-hidden rounded-2xl border border-m-border-light bg-m-surface md:grid-cols-2">
            <div className="min-h-[320px] bg-m-border-light md:min-h-[480px]" />
            <div className="space-y-4 p-8">
              <div className="h-8 w-2/3 rounded bg-m-border-light" />
              <div className="h-4 w-full rounded bg-m-border-light" />
              <div className="h-4 w-5/6 rounded bg-m-border-light" />
              <div className="h-12 w-40 rounded-full bg-m-border-light" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!spotlightVendor || spotlightVendors.length === 0) {
    return null;
  }

  const profileHref = spotlightVendor.vendorId
    ? `/vendor/${spotlightVendor.vendorId}`
    : `/services/${spotlightVendor.id}`;

  return (
    <section className="border-b border-m-border-light bg-m-canvas-light py-20 sm:py-24">
      <MarketingReveal className="page-container px-4 sm:px-6">
        <div className="mb-10 flex flex-col gap-4 sm:mb-12 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-xl">
            <p className="marketing-meta mb-3 text-[11px] uppercase tracking-[0.14em] text-m-ink-muted">
              {subtitle}
            </p>
            <h2 className="marketing-section-title text-m-ink">{title}</h2>
            <p className="marketing-body mt-3 text-m-ink-muted">
              Meet providers selected for quality, clarity, and consistent bookings.
            </p>
          </div>
          {spotlightVendors.length > 1 ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handlePrev}
                disabled={isAnimating}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-m-border-light bg-m-surface text-m-ink transition-colors hover:border-m-ink/30 disabled:opacity-50"
                aria-label="Previous vendor"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={handleNext}
                disabled={isAnimating}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-m-border-light bg-m-surface text-m-ink transition-colors hover:border-m-ink/30 disabled:opacity-50"
                aria-label="Next vendor"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          ) : null}
        </div>

        <div className="overflow-hidden rounded-2xl border border-m-border-light bg-m-surface">
          <div className="grid md:grid-cols-2">
            <div className="relative order-2 min-h-[280px] sm:min-h-[360px] md:order-1 md:min-h-[480px]">
              <img
                key={currentIndex}
                src={spotlightVendor.image}
                alt={spotlightVendor.name}
                className="absolute inset-0 h-full w-full object-cover transition-opacity duration-500"
                style={{ opacity: isAnimating ? 0.55 : 1 }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />

              {spotlightVendor.badge ? (
                <div className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-m-ink sm:left-6 sm:top-6">
                  <Award className="h-3.5 w-3.5" />
                  <span className="max-w-[160px] truncate">{spotlightVendor.badge}</span>
                </div>
              ) : null}

              <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-2 sm:bottom-6 sm:left-6 sm:right-6">
                <div className="inline-flex items-center gap-1.5 rounded-lg bg-black/45 px-3 py-1.5 text-white backdrop-blur-sm">
                  <Star className="h-3.5 w-3.5 fill-current" />
                  <span className="text-sm font-medium">{spotlightVendor.rating}</span>
                  <span className="text-xs text-white/75">
                    ({spotlightVendor.reviewCount})
                  </span>
                </div>
                <div className="inline-flex items-center gap-1.5 rounded-lg bg-black/45 px-3 py-1.5 text-white backdrop-blur-sm">
                  <Users className="h-3.5 w-3.5" />
                  <span className="text-sm font-medium">
                    {spotlightVendor.completedEvents}+
                  </span>
                  <span className="text-xs text-white/75">events</span>
                </div>
              </div>
            </div>

            <div
              className="order-1 flex flex-col justify-between p-6 transition-opacity duration-500 sm:p-8 md:order-2 md:p-10 lg:p-12"
              style={{ opacity: isAnimating ? 0.55 : 1 }}
            >
              <div key={`content-${currentIndex}`}>
                <div className="mb-5 flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <h3 className="marketing-display-md mb-2 text-m-ink">
                      {spotlightVendor.name}
                    </h3>
                    <p className="mb-2 text-sm font-medium text-m-ink-muted">
                      {spotlightVendor.category}
                    </p>
                    <div className="flex items-center gap-1.5 text-sm text-m-ink-muted">
                      <MapPin className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{spotlightVendor.location}</span>
                    </div>
                  </div>
                  <img
                    src={spotlightVendor.profileImage}
                    alt=""
                    className="h-14 w-14 shrink-0 rounded-full object-cover ring-1 ring-m-border-light sm:h-16 sm:w-16"
                  />
                </div>

                {spotlightVendor.description ? (
                  <p className="marketing-body mb-5 text-m-ink-muted">
                    {spotlightVendor.description}
                  </p>
                ) : null}

                {spotlightVendor.features?.length > 0 ? (
                  <div className="mb-6">
                    <h4 className="mb-3 text-sm font-medium text-m-ink">
                      What makes them special
                    </h4>
                    <ul className="space-y-2">
                      {spotlightVendor.features.slice(0, 5).map((feature, index) => (
                        <li
                          key={index}
                          className="flex items-start gap-2 text-sm text-m-ink-muted"
                        >
                          <CheckCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-m-ink" />
                          <span>{feature}</span>
                        </li>
                      ))}
                      {spotlightVendor.features.length > 5 ? (
                        <li className="text-xs text-m-ink-faint">
                          +{spotlightVendor.features.length - 5} more
                        </li>
                      ) : null}
                    </ul>
                  </div>
                ) : null}

                <div className="mb-6 grid grid-cols-2 gap-3 rounded-xl bg-m-canvas-light p-4">
                  <div>
                    <div className="text-2xl font-medium tracking-tight text-m-ink">
                      {spotlightVendor.yearsInBusiness}+
                    </div>
                    <div className="text-xs text-m-ink-muted">Years experience</div>
                  </div>
                  <div>
                    <div className="text-2xl font-medium tracking-tight text-m-ink">
                      ${spotlightVendor.startingPrice}+
                    </div>
                    <div className="text-xs text-m-ink-muted">Starting price</div>
                  </div>
                </div>
              </div>

              <Link
                to={profileHref}
                className="marketing-btn marketing-btn-ink w-full gap-2 sm:w-auto"
              >
                View full profile
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>

        {spotlightVendors.length > 1 ? (
          <div className="mt-8 flex items-center justify-center gap-2">
            {spotlightVendors.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => {
                  if (!isAnimating) {
                    setIsAnimating(true);
                    setCurrentIndex(index);
                    setTimeout(() => setIsAnimating(false), 450);
                  }
                }}
                className={`h-1.5 rounded-full transition-all ${
                  index === currentIndex
                    ? 'w-7 bg-m-ink'
                    : 'w-1.5 bg-m-border-light hover:bg-m-ink/40'
                }`}
                aria-label={`Go to vendor ${index + 1}`}
              />
            ))}
          </div>
        ) : null}
      </MarketingReveal>
    </section>
  );
};

export default VendorSpotlight;
